import React, { useState, useEffect } from 'react';
import AdminManagementService from '../services/adminManagementService';
import CreateAdminModal from '../components/admin/CreateAdminModal';
import ChangePasswordModal from '../components/admin/ChangePasswordModal';
import RoleManagement from '../components/admin/RoleManagement';
import { 
  Search, 
  User, 
  Mail, 
  Shield,
  UserPlus,
  Key,
  RefreshCw,
  MoreVertical,
  CheckCircle,
  XCircle,
  TrendingUp,
  Clock,
  Eye,
  Loader,
  AlertCircle,
  Crown,
  Settings,
  Users,
  Trash2,
  Lock,
  Unlock,
  Filter,
  BarChart3,
  ShieldCheck
} from 'lucide-react';

// Role-based permissions configuration
const ROLE_PERMISSIONS = {
  super_admin: {
    canManageAdmins: true,
    canCreateAdmins: true,
    canDeleteAdmins: true,
    canChangePasswords: true,
    canChangeStatus: true,
    canViewRoles: true,
    canManageRoles: true,
    permissions: [
      'Add/remove other admins and manage roles',
      'Edit FAQs, policies, and content',
      'Approve payouts and manage finances',
      'Approve/reject refunds',
      'Approve/reject tutor applications',
      'Ban/unban tutors and students',
      'View financial logs (read-only)',
      'Read tutor-student chats',
      'View refund requests',
      'View system logs and analytics',
      'View pending tutor applications',
      'View student/tutor profiles'
    ]
  },
  minister_admin: {
    canManageAdmins: false,
    canCreateAdmins: false,
    canDeleteAdmins: false,
    canChangePasswords: false,
    canChangeStatus: false,
    canViewRoles: true,
    canManageRoles: false,
    permissions: [
      'Edit FAQs, policies, and content',
      'View financial logs (read-only)',
      'View pending tutor applications',
      'View student/tutor profiles'
    ]
  },
  support_admin: {
    canManageAdmins: false,
    canCreateAdmins: false,
    canDeleteAdmins: false,
    canChangePasswords: false,
    canChangeStatus: false,
    canViewRoles: false,
    canManageRoles: false,
    permissions: [
      'View refund requests',
      'View student/tutor profiles'
    ]
  }
};

// FIXED: Get current admin role from your authentication system
// This should come from your auth context, localStorage, or API
const getCurrentAdminRole = () => {
  // Try to get from localStorage (you should set this during login)
  const storedAdmin = localStorage.getItem('currentAdmin');
  if (storedAdmin) {
    try {
      const adminData = JSON.parse(storedAdmin);
      return adminData.role || 'support_admin';
    } catch (error) {
      console.error('Error parsing stored admin data:', error);
    }
  }
  
  // Try to get from session storage
  const sessionAdmin = sessionStorage.getItem('currentAdmin');
  if (sessionAdmin) {
    try {
      const adminData = JSON.parse(sessionAdmin);
      return adminData.role || 'support_admin';
    } catch (error) {
      console.error('Error parsing session admin data:', error);
    }
  }
  
  // Fallback - you might want to make an API call to /api/admin/auth/profile
  console.warn('No admin role found in storage, defaulting to support_admin');
  return 'support_admin';
};

// FIXED: Get current admin data (for display purposes)
const getCurrentAdminData = () => {
  const storedAdmin = localStorage.getItem('currentAdmin') || sessionStorage.getItem('currentAdmin');
  if (storedAdmin) {
    try {
      return JSON.parse(storedAdmin);
    } catch (error) {
      console.error('Error parsing admin data:', error);
    }
  }
  return { email: 'Unknown', role: 'support_admin' };
};

const AdminCard = ({ admin, currentUserRole, onAction, loading }) => {
  const [showActions, setShowActions] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const currentPermissions = ROLE_PERMISSIONS[currentUserRole];

  const getRoleConfig = (role) => {
    const configs = {
      super_admin: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: Crown,
        label: 'Super Admin',
        badgeColor: 'from-red-500 to-red-600'
      },
      minister_admin: {
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Settings,
        label: 'Minister Admin',
        badgeColor: 'from-blue-500 to-blue-600'
      },
      support_admin: {
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: Users,
        label: 'Support Admin',
        badgeColor: 'from-green-500 to-green-600'
      }
    };
    return configs[role] || configs.support_admin;
  };

  const getStatusConfig = (status) => {
    const configs = {
      active: {
        color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
        icon: CheckCircle,
        label: 'Active'
      },
      inactive: {
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        label: 'Inactive'
      }
    };
    return configs[status] || configs.inactive;
  };

  const roleConfig = getRoleConfig(admin.role);
  const statusConfig = getStatusConfig(admin.status);
  const RoleIcon = roleConfig.icon;
  const StatusIcon = statusConfig.icon;

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // FIXED: Enhanced permission checks
  const canPerformActions = currentPermissions.canManageAdmins && admin.role !== 'super_admin';
  const canDelete = currentPermissions.canDeleteAdmins && admin.role !== 'super_admin';
  const canChangePassword = currentPermissions.canChangePasswords && admin.role !== 'super_admin';

  return (
    <div className="bg-white rounded-2xl border border-gray-200 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
      <div className="p-6">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Header with role badge */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className={`p-3 rounded-2xl bg-gradient-to-r ${roleConfig.badgeColor}`}>
                  <RoleIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {admin.email}
                  </h3>
                  <p className="text-gray-500 text-sm">ID: {admin.id}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${roleConfig.color} border`}>
                  <RoleIcon className="h-4 w-4 mr-2" />
                  {roleConfig.label}
                </span>
                <span className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-semibold ${statusConfig.color} border`}>
                  <StatusIcon className="h-4 w-4 mr-2" />
                  {statusConfig.label}
                </span>
              </div>
            </div>

            {/* Quick Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                  <Mail className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Email</p>
                    <p className="text-gray-900 font-semibold">{admin.email}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Last Login</p>
                    <p className="text-gray-900 font-semibold">
                      {admin.last_login ? formatDate(admin.last_login) : 'Never logged in'}
                    </p>
                  </div>
                </div>
              </div>
              <div className="space-y-3">
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                  <Shield className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Access Level</p>
                    <p className="text-gray-900 font-semibold">
                      {admin.role === 'super_admin' ? 'Full System Access' : 
                       admin.role === 'minister_admin' ? 'Content & User Management' : 'Basic Support Access'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3 p-3 bg-gray-50 rounded-xl">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-600">Created</p>
                    <p className="text-gray-900 font-semibold">{formatDate(admin.created_at)}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Actions - FIXED: Only show for Super Admin on non-Super Admin accounts */}
            {canPerformActions && (
              <div className="flex items-center space-x-3 mb-4">
                {canChangePassword && (
                  <button
                    onClick={() => onAction('changePassword', admin)}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                  >
                    <Key className="h-4 w-4" />
                    <span>Change Password</span>
                  </button>
                )}
                
                {currentPermissions.canChangeStatus && (
                  <>
                    {admin.status === 'active' ? (
                      <button
                        onClick={() => onAction('deactivate', admin)}
                        disabled={loading}
                        className="flex items-center space-x-2 px-4 py-2 bg-amber-50 text-amber-700 rounded-lg hover:bg-amber-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                      >
                        <Lock className="h-4 w-4" />
                        <span>Deactivate</span>
                      </button>
                    ) : (
                      <button
                        onClick={() => onAction('activate', admin)}
                        disabled={loading}
                        className="flex items-center space-x-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-lg hover:bg-emerald-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                      >
                        <Unlock className="h-4 w-4" />
                        <span>Activate</span>
                      </button>
                    )}
                  </>
                )}

                {/* FIXED: Delete button for Super Admin */}
                {canDelete && (
                  <button
                    onClick={() => onAction('delete', admin)}
                    disabled={loading}
                    className="flex items-center space-x-2 px-4 py-2 bg-red-50 text-red-700 rounded-lg hover:bg-red-100 disabled:opacity-50 transition-colors duration-200 font-medium"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Delete Admin</span>
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Action Menu */}
          <div className="relative ml-4">
            <button
              onClick={() => setShowActions(!showActions)}
              className="p-3 text-gray-400 hover:text-gray-600 rounded-xl hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
            >
              <MoreVertical className="h-5 w-5" />
            </button>

            {showActions && (
              <div className="absolute right-0 top-12 w-64 bg-white rounded-2xl border border-gray-200 shadow-2xl z-20 py-3">
                <button
                  onClick={() => {
                    setShowDetails(!showDetails);
                    setShowActions(false);
                  }}
                  className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                >
                  <Eye className="h-4 w-4" />
                  <span>View Details</span>
                </button>
                
                {canPerformActions && (
                  <>
                    {canChangePassword && (
                      <button
                        onClick={() => {
                          onAction('changePassword', admin);
                          setShowActions(false);
                        }}
                        disabled={loading}
                        className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50 transition-colors duration-200"
                      >
                        <Key className="h-4 w-4" />
                        <span>Change Password</span>
                      </button>
                    )}
                    
                    {currentPermissions.canChangeStatus && (
                      <>
                        {admin.status === 'active' ? (
                          <button
                            onClick={() => {
                              onAction('deactivate', admin);
                              setShowActions(false);
                            }}
                            disabled={loading}
                            className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-amber-600 hover:bg-amber-50 disabled:opacity-50 transition-colors duration-200"
                          >
                            <Lock className="h-4 w-4" />
                            <span>Deactivate Account</span>
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              onAction('activate', admin);
                              setShowActions(false);
                            }}
                            disabled={loading}
                            className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-emerald-600 hover:bg-emerald-50 disabled:opacity-50 transition-colors duration-200"
                          >
                            <Unlock className="h-4 w-4" />
                            <span>Activate Account</span>
                          </button>
                        )}
                      </>
                    )}

                    {/* FIXED: Delete option in menu */}
                    {canDelete && (
                      <div className="border-t border-gray-200 mt-2 pt-2">
                        <button
                          onClick={() => {
                            onAction('delete', admin);
                            setShowActions(false);
                          }}
                          disabled={loading}
                          className="flex items-center space-x-3 w-full px-4 py-3 text-sm text-red-600 hover:bg-red-50 disabled:opacity-50 transition-colors duration-200"
                        >
                          <Trash2 className="h-4 w-4" />
                          <span>Delete Account</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Detailed Permissions View */}
        {showDetails && (
          <div className="mt-6 p-6 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-bold text-gray-900">Role Permissions & Details</h4>
              <ShieldCheck className="h-5 w-5 text-blue-500" />
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div>
                <h5 className="font-semibold text-gray-700 mb-3">System Permissions</h5>
                <div className="space-y-2">
                  {ROLE_PERMISSIONS[admin.role]?.permissions.map((permission, index) => (
                    <div key={index} className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
                      <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{permission}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <h5 className="font-semibold text-gray-700 mb-3">Account Information</h5>
                <div className="space-y-3 bg-white p-4 rounded-lg border border-gray-200">
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Admin ID</span>
                    <span className="font-mono font-semibold text-gray-900">{admin.id}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Email Address</span>
                    <span className="font-semibold text-gray-900">{admin.email}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Role Level</span>
                    <span className="font-semibold capitalize text-gray-900">{admin.role?.replace('_', ' ')}</span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Account Created</span>
                    <span className="font-semibold text-gray-900">{formatDate(admin.created_at)}</span>
                  </div>
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600">Last Activity</span>
                    <span className="font-semibold text-gray-900">
                      {admin.last_login ? formatDate(admin.last_login) : 'No activity'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const StatCard = ({ title, value, icon: Icon, color = 'blue', trend, subtitle, description }) => {
  const colorConfigs = {
    blue: { 
      bg: 'bg-blue-50', 
      iconBg: 'bg-blue-100',
      icon: 'text-blue-600', 
      text: 'text-blue-700', 
      border: 'border-blue-200',
      gradient: 'from-blue-500 to-blue-600'
    },
    emerald: { 
      bg: 'bg-emerald-50',
      iconBg: 'bg-emerald-100',
      icon: 'text-emerald-600', 
      text: 'text-emerald-700', 
      border: 'border-emerald-200',
      gradient: 'from-emerald-500 to-emerald-600'
    },
    amber: { 
      bg: 'bg-amber-50',
      iconBg: 'bg-amber-100',
      icon: 'text-amber-600', 
      text: 'text-amber-700', 
      border: 'border-amber-200',
      gradient: 'from-amber-500 to-amber-600'
    },
    red: { 
      bg: 'bg-red-50',
      iconBg: 'bg-red-100',
      icon: 'text-red-600', 
      text: 'text-red-700', 
      border: 'border-red-200',
      gradient: 'from-red-500 to-red-600'
    }
  };

  const config = colorConfigs[color] || colorConfigs.blue;

  return (
    <div className={`${config.bg} ${config.border} rounded-2xl border p-6 hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}>
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-600 mb-2">{title}</p>
          <p className={`text-3xl font-bold ${config.text} mb-2`}>{value}</p>
          {subtitle && (
            <p className="text-sm text-gray-500 mb-1">{subtitle}</p>
          )}
          {description && (
            <p className="text-xs text-gray-400">{description}</p>
          )}
          {trend && (
            <div className={`flex items-center space-x-1 mt-3 text-sm font-medium ${
              trend.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
            }`}>
              <TrendingUp className={`h-4 w-4 ${trend.direction === 'down' ? 'rotate-180' : ''}`} />
              <span>{trend.value}</span>
            </div>
          )}
        </div>
        <div className={`p-4 rounded-2xl ${config.iconBg}`}>
          <Icon className={`h-6 w-6 ${config.icon}`} />
        </div>
      </div>
    </div>
  );
};

const AdminManagement = () => {
  const [activeTab, setActiveTab] = useState('admins');
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    search: '',
    role: 'all'
  });
  const [statistics, setStatistics] = useState({
    totalAdmins: 0,
    activeAdmins: 0,
    inactiveAdmins: 0,
    superAdmins: 0,
    ministerAdmins: 0,
    supportAdmins: 0
  });
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  
  // FIXED: Get current admin data properly
  const [currentAdminData, setCurrentAdminData] = useState(() => getCurrentAdminData());
  const currentUserRole = currentAdminData.role;
  const currentPermissions = ROLE_PERMISSIONS[currentUserRole];

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Fetching admins...');
      const response = await AdminManagementService.getAdmins();
      
      if (response.success) {
        console.log('✅ Admins fetched successfully:', response.data.admins);
        const adminsData = response.data.admins || [];
        setAdmins(adminsData);
        
        setStatistics({
          totalAdmins: adminsData.length,
          activeAdmins: adminsData.filter(a => a.status === 'active').length,
          inactiveAdmins: adminsData.filter(a => a.status === 'inactive').length,
          superAdmins: adminsData.filter(a => a.role === 'super_admin').length,
          ministerAdmins: adminsData.filter(a => a.role === 'minister_admin').length,
          supportAdmins: adminsData.filter(a => a.role === 'support_admin').length
        });
      } else {
        console.error('❌ API returned error:', response.message);
        setError(response.message || 'Failed to load admins');
      }
    } catch (err) {
      console.error('❌ Failed to fetch admins:', err);
      setError('Failed to load admin accounts. Please check your connection.');
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'admins') {
      fetchAdmins();
    }
  }, [activeTab]);

  const handleAdminAction = async (action, admin) => {
    switch (action) {
      case 'changePassword':
        setSelectedAdmin(admin);
        setShowPasswordModal(true);
        break;
        
      case 'activate':
        await handleChangeStatus(admin.id, 'active');
        break;
        
      case 'deactivate':
        await handleChangeStatus(admin.id, 'inactive');
        break;
        
      case 'delete':
        await handleDeleteAdmin(admin.id);
        break;
        
      default:
        break;
    }
  };

  const handleCreateAdmin = async (adminData) => {
    try {
      console.log('🔄 Creating admin:', adminData);
      const response = await AdminManagementService.createAdmin(adminData);
      
      if (response.success) {
        alert('Admin created successfully!');
        setShowCreateModal(false);
        fetchAdmins();
      } else {
        alert(`Failed to create admin: ${response.message}`);
      }
    } catch (error) {
      console.error('Error creating admin:', error);
      alert('Failed to create admin. Please try again.');
    }
  };

  const handleChangeStatus = async (adminId, newStatus) => {
    const action = newStatus === 'active' ? 'activate' : 'deactivate';
    if (!window.confirm(`Are you sure you want to ${action} this admin?`)) {
      return;
    }

    try {
      setProcessing(prev => ({ ...prev, [adminId]: true }));
      const response = await AdminManagementService.updateAdmin(adminId, { status: newStatus });
      
      if (response.success) {
        alert(`Admin ${action}d successfully!`);
        fetchAdmins();
      } else {
        alert(`Failed to ${action} admin: ${response.message}`);
      }
    } catch (error) {
      console.error(`Error ${action}ing admin:`, error);
      alert(`Failed to ${action} admin. Please try again.`);
    } finally {
      setProcessing(prev => ({ ...prev, [adminId]: false }));
    }
  };

  // FIXED: Enhanced delete function with better confirmation
  const handleDeleteAdmin = async (adminId) => {
    const adminToDelete = admins.find(admin => admin.id === adminId);
    if (!adminToDelete) return;

    if (!window.confirm(
      `Are you sure you want to permanently delete the admin account:\n\n` +
      `Email: ${adminToDelete.email}\n` +
      `Role: ${adminToDelete.role}\n\n` +
      `This action cannot be undone and will immediately revoke all access for this admin.`
    )) {
      return;
    }

    try {
      setProcessing(prev => ({ ...prev, [adminId]: true }));
      const response = await AdminManagementService.deleteAdmin(adminId);
      
      if (response.success) {
        alert('Admin account deleted successfully!');
        fetchAdmins();
      } else {
        alert(`Failed to delete admin: ${response.message}`);
      }
    } catch (error) {
      console.error('Error deleting admin:', error);
      alert('Failed to delete admin. Please try again.');
    } finally {
      setProcessing(prev => ({ ...prev, [adminId]: false }));
    }
  };

  const handleChangePassword = async (adminId, newPassword) => {
    try {
      const response = await AdminManagementService.changeAdminPassword(adminId, newPassword);
      
      if (response.success) {
        alert('Password changed successfully!');
        setShowPasswordModal(false);
        setSelectedAdmin(null);
      } else {
        alert(`Failed to change password: ${response.message}`);
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password. Please try again.');
    }
  };

  const filteredAdmins = admins.filter(admin => {
    const matchesSearch = admin.email.toLowerCase().includes(filters.search.toLowerCase()) ||
                         admin.role.toLowerCase().includes(filters.search.toLowerCase()) ||
                         admin.id.toString().includes(filters.search);
    const matchesStatus = filters.status === 'all' || admin.status === filters.status;
    const matchesRole = filters.role === 'all' || admin.role === filters.role;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const stats = {
    total: statistics.totalAdmins,
    active: statistics.activeAdmins,
    inactive: statistics.inactiveAdmins,
    super: statistics.superAdmins,
    minister: statistics.ministerAdmins,
    support: statistics.supportAdmins
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="flex items-center space-x-4 mb-2">
              <div className="p-3 bg-white rounded-2xl shadow-lg">
                <ShieldCheck className="h-8 w-8 text-blue-600" />
              </div>
              <div>
                <h1 className="text-4xl font-bold text-gray-900">Admin Management</h1>
                <p className="text-gray-600 mt-2 text-lg">Manage admin accounts, roles, and system permissions</p>
              </div>
            </div>
            <div className="flex items-center space-x-4 mt-4">
              {/* FIXED: Display actual current admin role */}
              <span className="inline-flex items-center px-4 py-2 bg-white rounded-full text-sm font-semibold text-gray-700 border border-gray-200">
                <User className="h-4 w-4 mr-2" />
                Logged in as: <span className="ml-1 font-bold capitalize">{currentUserRole.replace('_', ' ')}</span>
              </span>
              {currentPermissions.canManageAdmins && (
                <span className="inline-flex items-center px-4 py-2 bg-emerald-50 rounded-full text-sm font-semibold text-emerald-700">
                  <Shield className="h-4 w-4 mr-2" />
                  Full Management Access
                </span>
              )}
              {/* FIXED: Show create admin capability for Super Admin */}
              {currentPermissions.canCreateAdmins && (
                <span className="inline-flex items-center px-4 py-2 bg-blue-50 rounded-full text-sm font-semibold text-blue-700">
                  <UserPlus className="h-4 w-4 mr-2" />
                  Can Create/Delete Admins
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-3 mt-6 lg:mt-0">
            <button
              onClick={fetchAdmins}
              className="flex items-center space-x-2 px-6 py-3 bg-white border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-all duration-200 font-semibold shadow-sm hover:shadow-md"
            >
              <RefreshCw className="h-5 w-5" />
              <span>Refresh Data</span>
            </button>
            {/* FIXED: Create Admin button only for Super Admin */}
            {currentPermissions.canCreateAdmins && (
              <button 
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
              >
                <UserPlus className="h-5 w-5" />
                <span>Create New Admin</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Rest of the component remains the same... */}
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-2xl p-6 mb-8 shadow-lg">
          <div className="flex items-center space-x-4">
            <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-red-800 font-bold text-lg">Unable to load admins</p>
              <p className="text-red-600">{error}</p>
            </div>
            <button
              onClick={fetchAdmins}
              className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors duration-200 font-semibold"
            >
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-2xl border border-gray-200 p-2 mb-8 shadow-lg">
        <nav className="flex space-x-2">
          <button
            onClick={() => setActiveTab('admins')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
              activeTab === 'admins'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <Users className="h-5 w-5" />
            <span>Admin Users</span>
          </button>
          {currentPermissions.canViewRoles && (
            <button
              onClick={() => setActiveTab('roles')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                activeTab === 'roles'
                  ? 'bg-blue-600 text-white shadow-lg'
                  : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
              }`}
            >
              <Shield className="h-5 w-5" />
              <span>Role Management</span>
            </button>
          )}
          <button
            onClick={() => setActiveTab('analytics')}
            className={`flex items-center space-x-2 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
              activeTab === 'analytics'
                ? 'bg-blue-600 text-white shadow-lg'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <BarChart3 className="h-5 w-5" />
            <span>Analytics</span>
          </button>
        </nav>
      </div>

      {/* Admins Tab Content */}
      {activeTab === 'admins' && (
        <>
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <StatCard
              title="Total Admins"
              value={stats.total}
              icon={Users}
              color="blue"
              description="All admin accounts"
            />
            <StatCard
              title="Active Admins"
              value={stats.active}
              icon={CheckCircle}
              color="emerald"
              subtitle={`${stats.inactive} inactive`}
              description="Currently active accounts"
            />
            <StatCard
              title="Super Admins"
              value={stats.super}
              icon={Crown}
              color="red"
              description="Full system access"
            />
            <StatCard
              title="Support Team"
              value={stats.support}
              icon={User}
              color="amber"
              description="Limited access accounts"
            />
          </div>

          {/* Advanced Filters */}
          <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-8 shadow-lg">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Filter Admins</h3>
              <Filter className="h-5 w-5 text-gray-400" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Search Admins</label>
                <div className="relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by email, role, or ID..."
                    value={filters.search}
                    onChange={(e) => setFilters(prev => ({ ...prev, search: e.target.value }))}
                    className="pl-12 w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                  />
                </div>
              </div>

              {/* Status */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active Only</option>
                  <option value="inactive">Inactive Only</option>
                </select>
              </div>

              {/* Role */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-3">Role</label>
                <select
                  value={filters.role}
                  onChange={(e) => setFilters(prev => ({ ...prev, role: e.target.value }))}
                  className="w-full px-4 py-4 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 bg-gray-50 focus:bg-white"
                >
                  <option value="all">All Roles</option>
                  <option value="super_admin">Super Admin</option>
                  <option value="minister_admin">Minister Admin</option>
                  <option value="support_admin">Support Admin</option>
                </select>
              </div>

              {/* Results Count */}
              <div className="flex items-end">
                <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-4 w-full border border-gray-200">
                  <p className="text-sm font-semibold text-gray-700">
                    Showing <span className="text-blue-600 font-bold text-lg">{filteredAdmins.length}</span> of{' '}
                    <span className="text-gray-900 font-bold">{admins.length}</span> admins
                  </p>
                  <p className="text-xs text-gray-500 mt-1">Filtered results</p>
                </div>
              </div>
            </div>
          </div>

          {/* Admins List */}
          <div className="space-y-6">
            {loading ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-lg">
                <Loader className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Loading Admin Accounts</h3>
                <p className="text-gray-600 text-lg">Fetching admin accounts from the server...</p>
              </div>
            ) : filteredAdmins.length > 0 ? (
              filteredAdmins.map((admin) => (
                <AdminCard
                  key={admin.id}
                  admin={admin}
                  currentUserRole={currentUserRole}
                  onAction={handleAdminAction}
                  loading={processing[admin.id]}
                />
              ))
            ) : (
              <div className="bg-white rounded-2xl border border-gray-200 p-16 text-center shadow-lg">
                <User className="h-20 w-20 text-gray-400 mx-auto mb-6" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">No Admin Accounts Found</h3>
                <p className="text-gray-600 text-lg mb-8">
                  {filters.search || filters.status !== 'all' || filters.role !== 'all'
                    ? 'Try adjusting your search criteria or filters to see more results.' 
                    : 'No admin accounts are currently registered in the system.'}
                </p>
                {(filters.search || filters.status !== 'all' || filters.role !== 'all') && (
                  <button
                    onClick={() => setFilters({ search: '', status: 'all', role: 'all' })}
                    className="px-8 py-4 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl"
                  >
                    Clear All Filters
                  </button>
                )}
                {currentPermissions.canCreateAdmins && admins.length === 0 && (
                  <button
                    onClick={() => setShowCreateModal(true)}
                    className="px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-semibold text-lg shadow-lg hover:shadow-xl mt-4"
                  >
                    Create First Admin
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Roles Tab Content */}
      {activeTab === 'roles' && currentPermissions.canViewRoles && (
        <RoleManagement currentUserRole={currentUserRole} />
      )}

      {/* Analytics Tab Content */}
      {activeTab === 'analytics' && (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-lg">
          <div className="text-center py-12">
            <BarChart3 className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">Analytics Dashboard</h3>
            <p className="text-gray-600">Admin activity analytics and reports coming soon...</p>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateModal && currentPermissions.canCreateAdmins && (
        <CreateAdminModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={handleCreateAdmin}
          currentUserRole={currentUserRole}
        />
      )}

      {showPasswordModal && selectedAdmin && (
        <ChangePasswordModal
          admin={selectedAdmin}
          onClose={() => {
            setShowPasswordModal(false);
            setSelectedAdmin(null);
          }}
          onSubmit={handleChangePassword}
        />
      )}
    </div>
  );
};

export default AdminManagement;